import { MAX_PACKET_BYTES, concatBytes, int32LE, int64LE, readInt32LE, readInt64LE, uint32LE, } from "./bytes.js";
import { TlReader, encodeTlBytes, encodeTlVector } from "./tl.js";
export const MAX_CONTAINER_MESSAGES = 1024;
export const MAX_SERVICE_MESSAGE_IDS = 8192;
export const MAX_GZIP_OUTPUT_BYTES = MAX_PACKET_BYTES;
export const ServiceConstructor = {
    rpcResult: 0xf35c6d01,
    rpcError: 0x2144ca19,
    pong: 0x347773c5,
    newSessionCreated: 0x9ec20908,
    gzipPacked: 0x3072cfa1,
    msgsAck: 0x62d6b459,
    badMsgNotification: 0xa7eff811,
    badServerSalt: 0xedab447b,
    msgResendReq: 0x7d861a08,
    msgsStateReq: 0xda69fb52,
    msgsStateInfo: 0x04deb57d,
    msgsAllInfo: 0x8cc0d131,
    msgDetailedInfo: 0x276d3ec6,
    msgNewDetailedInfo: 0x809db6df,
    msgContainer: 0x73f1f8dc,
    msgCopy: 0xe06046b2,
    ping: 0x7abe77ec,
    pingDelayDisconnect: 0xf3427b8c,
    destroySession: 0xe7512126,
    destroySessionOk: 0xe22045fc,
    destroySessionNone: 0x62d350c9,
    destroyAuthKey: 0xd1435160,
    destroyAuthKeyOk: 0xf660e1d4,
    destroyAuthKeyNone: 0x0a9f2259,
    destroyAuthKeyFail: 0xea109b13,
    getFutureSalts: 0xb921bd04,
    futureSalt: 0x0949d9dc,
    futureSalts: 0xae500895,
    invokeAfterMsg: 0xcb9f372d,
    invokeAfterMsgs: 0x3dc4b4f0,
    rpcDropAnswer: 0x58e4a740,
    rpcAnswerUnknown: 0x5e2ad36e,
    rpcAnswerDroppedRunning: 0xcd78e586,
    rpcAnswerDropped: 0xa43ad8b7,
    httpWait: 0x9299359f,
};
const constructor = (value) => uint32LE(value);
const fixedBody = (id, ...parts) => concatBytes(constructor(id), ...parts);
const requireAlignedBody = (body) => {
    if (body.length < 4 || body.length > MAX_PACKET_BYTES || body.length % 4 !== 0) {
        throw new RangeError("MTProto body must be non-empty, bounded, and four-byte aligned");
    }
};
const encodeMessageIds = (id, messageIds) => {
    if (messageIds.length > MAX_SERVICE_MESSAGE_IDS)
        throw new RangeError("Too many service message IDs");
    return fixedBody(id, encodeTlVector(messageIds.map(int64LE)));
};
const decodeMessageIds = (body, expected) => {
    const reader = readerFor(body, expected);
    const ids = reader.readVector((item) => item.readLong(), MAX_SERVICE_MESSAGE_IDS);
    reader.expectEnd();
    return ids;
};
export const encodeMessageContainer = (messages) => {
    if (messages.length < 1 || messages.length > MAX_CONTAINER_MESSAGES) {
        throw new RangeError("Invalid message-container count");
    }
    const encoded = messages.map((message) => {
        requireAlignedBody(message.body);
        if ((readInt32LE(message.body, 0) >>> 0) === ServiceConstructor.msgContainer) {
            throw new RangeError("Nested message containers are forbidden");
        }
        return concatBytes(int64LE(message.messageId), int32LE(message.sequenceNumber), int32LE(message.body.length), message.body);
    });
    return fixedBody(ServiceConstructor.msgContainer, int32LE(messages.length), ...encoded);
};
export const decodeMessageContainer = (body) => {
    const reader = readerFor(body, ServiceConstructor.msgContainer);
    const count = reader.readInt();
    if (count < 1 || count > MAX_CONTAINER_MESSAGES)
        throw new RangeError("Invalid message-container count");
    const messages = [];
    for (let index = 0; index < count; index += 1) {
        const messageId = reader.readLong();
        const sequenceNumber = reader.readInt();
        const bytes = reader.readInt();
        if (bytes < 4 || bytes > MAX_PACKET_BYTES || bytes % 4 !== 0)
            throw new RangeError("Invalid contained body length");
        const child = reader.readFixed(bytes);
        if ((readInt32LE(child, 0) >>> 0) === ServiceConstructor.msgContainer) {
            throw new RangeError("Nested message containers are forbidden");
        }
        messages.push({ messageId, sequenceNumber, body: child });
    }
    reader.expectEnd();
    return messages;
};
export const encodeMsgCopy = (message) => {
    requireAlignedBody(message.body);
    return fixedBody(ServiceConstructor.msgCopy, int64LE(message.messageId), int32LE(message.sequenceNumber), int32LE(message.body.length), message.body);
};
export const decodeMsgCopy = (body) => {
    const reader = readerFor(body, ServiceConstructor.msgCopy);
    const messageId = reader.readLong();
    const sequenceNumber = reader.readInt();
    const length = reader.readInt();
    if (length < 4 || length > MAX_PACKET_BYTES || length % 4 !== 0) {
        throw new RangeError("Invalid copied body length");
    }
    const copied = reader.readFixed(length);
    reader.expectEnd();
    if ((readInt32LE(copied, 0) >>> 0) === ServiceConstructor.msgContainer) {
        throw new RangeError("A copied message cannot contain a container");
    }
    return { messageId, sequenceNumber, body: copied };
};
export const encodeInvokeAfterMsg = (messageId, query) => {
    requireAlignedBody(query);
    return fixedBody(ServiceConstructor.invokeAfterMsg, int64LE(messageId), query);
};
export const encodeInvokeAfterMsgs = (messageIds, query) => {
    if (messageIds.length > MAX_SERVICE_MESSAGE_IDS)
        throw new RangeError("Too many invoke-after dependencies");
    requireAlignedBody(query);
    return fixedBody(ServiceConstructor.invokeAfterMsgs, encodeTlVector(messageIds.map(int64LE)), query);
};
export const decodeInvokeAfter = (body) => {
    const id = serviceConstructor(body);
    const reader = readerFor(body, id);
    let messageIds;
    if (id === ServiceConstructor.invokeAfterMsg) {
        messageIds = [reader.readLong()];
    }
    else if (id === ServiceConstructor.invokeAfterMsgs) {
        messageIds = reader.readVector((item) => item.readLong(), MAX_SERVICE_MESSAGE_IDS);
    }
    else {
        throw new RangeError("Unexpected invoke-after constructor");
    }
    const query = reader.readFixed(reader.remaining);
    requireAlignedBody(query);
    return { messageIds, query };
};
export const encodeRpcResult = (requestMessageId, result) => {
    requireAlignedBody(result);
    return fixedBody(ServiceConstructor.rpcResult, int64LE(requestMessageId), result);
};
export const decodeRpcResult = (body) => {
    const reader = readerFor(body, ServiceConstructor.rpcResult);
    const requestMessageId = reader.readLong();
    const result = reader.readFixed(reader.remaining);
    requireAlignedBody(result);
    return { requestMessageId, result };
};
export const encodeRpcError = (code, message) => fixedBody(ServiceConstructor.rpcError, int32LE(code), encodeTlBytes(new TextEncoder().encode(message)));
export const decodeRpcError = (body) => {
    const reader = readerFor(body, ServiceConstructor.rpcError);
    const code = reader.readInt();
    const message = new TextDecoder().decode(reader.readBytes());
    reader.expectEnd();
    return { code, message };
};
export const encodeRpcDropAnswer = (requestMessageId) => fixedBody(ServiceConstructor.rpcDropAnswer, int64LE(requestMessageId));
export const decodeRpcDropAnswer = (body) => {
    const reader = readerFor(body, ServiceConstructor.rpcDropAnswer);
    const requestMessageId = reader.readLong();
    reader.expectEnd();
    return requestMessageId;
};
export const encodeRpcDropAnswerResult = (result) => {
    if (result.kind === "unknown")
        return fixedBody(ServiceConstructor.rpcAnswerUnknown);
    if (result.kind === "running")
        return fixedBody(ServiceConstructor.rpcAnswerDroppedRunning);
    if (!Number.isSafeInteger(result.bytes) || result.bytes < 0 || result.bytes > MAX_PACKET_BYTES) {
        throw new RangeError("Invalid dropped-answer byte count");
    }
    return fixedBody(ServiceConstructor.rpcAnswerDropped, int64LE(result.messageId), int32LE(result.sequenceNumber), int32LE(result.bytes));
};
export const decodeRpcDropAnswerResult = (body) => {
    const id = serviceConstructor(body);
    if (id === ServiceConstructor.rpcAnswerUnknown && body.length === 4)
        return { kind: "unknown" };
    if (id === ServiceConstructor.rpcAnswerDroppedRunning && body.length === 4)
        return { kind: "running" };
    const reader = readerFor(body, ServiceConstructor.rpcAnswerDropped);
    const messageId = reader.readLong();
    const sequenceNumber = reader.readInt();
    const bytes = reader.readInt();
    reader.expectEnd();
    if (bytes < 0 || bytes > MAX_PACKET_BYTES)
        throw new RangeError("Invalid dropped-answer byte count");
    return { kind: "dropped", messageId, sequenceNumber, bytes };
};
export const encodeHttpWait = (input) => {
    for (const value of [input.maximumDelay, input.waitAfter, input.maximumWait]) {
        if (!Number.isSafeInteger(value) || value < 0)
            throw new RangeError("Invalid HTTP wait interval");
    }
    return fixedBody(ServiceConstructor.httpWait, int32LE(input.maximumDelay), int32LE(input.waitAfter), int32LE(input.maximumWait));
};
export const decodeHttpWait = (body) => {
    const reader = readerFor(body, ServiceConstructor.httpWait);
    const result = {
        maximumDelay: reader.readInt(),
        waitAfter: reader.readInt(),
        maximumWait: reader.readInt(),
    };
    reader.expectEnd();
    if (Object.values(result).some((value) => value < 0))
        throw new RangeError("Invalid HTTP wait interval");
    return result;
};
export const encodeMsgsAck = (messageIds) => encodeMessageIds(ServiceConstructor.msgsAck, messageIds);
export const decodeMsgsAck = (body) => decodeMessageIds(body, ServiceConstructor.msgsAck);
export const encodeMsgResendReq = (messageIds) => encodeMessageIds(ServiceConstructor.msgResendReq, messageIds);
export const decodeMsgResendReq = (body) => decodeMessageIds(body, ServiceConstructor.msgResendReq);
export const encodeMsgsStateReq = (messageIds) => encodeMessageIds(ServiceConstructor.msgsStateReq, messageIds);
export const decodeMsgsStateReq = (body) => decodeMessageIds(body, ServiceConstructor.msgsStateReq);
export const encodeMsgsStateInfo = (requestMessageId, states) => {
    if (states.length > MAX_SERVICE_MESSAGE_IDS)
        throw new RangeError("Too many message states");
    return fixedBody(ServiceConstructor.msgsStateInfo, int64LE(requestMessageId), encodeTlBytes(states));
};
export const decodeMsgsStateInfo = (body) => {
    const reader = readerFor(body, ServiceConstructor.msgsStateInfo);
    const requestMessageId = reader.readLong();
    const states = reader.readBytes();
    reader.expectEnd();
    if (states.length > MAX_SERVICE_MESSAGE_IDS)
        throw new RangeError("Too many message states");
    return { requestMessageId, states };
};
export const encodeMsgsAllInfo = (messageIds, states) => {
    if (messageIds.length !== states.length || messageIds.length > MAX_SERVICE_MESSAGE_IDS) {
        throw new RangeError("Message-state cardinality mismatch");
    }
    return fixedBody(ServiceConstructor.msgsAllInfo, encodeTlVector(messageIds.map(int64LE)), encodeTlBytes(states));
};
export const decodeMsgsAllInfo = (body) => {
    const reader = readerFor(body, ServiceConstructor.msgsAllInfo);
    const messageIds = reader.readVector((item) => item.readLong(), MAX_SERVICE_MESSAGE_IDS);
    const states = reader.readBytes();
    reader.expectEnd();
    if (messageIds.length !== states.length)
        throw new RangeError("Message-state cardinality mismatch");
    return { messageIds, states };
};
export const encodeDetailedMessageInfo = (info) => {
    if (!Number.isSafeInteger(info.bytes) || info.bytes < 0 || info.bytes > MAX_PACKET_BYTES ||
        !Number.isSafeInteger(info.status) || info.status < 0) {
        throw new RangeError("Invalid detailed message information");
    }
    return info.messageId === undefined
        ? fixedBody(ServiceConstructor.msgNewDetailedInfo, int64LE(info.answerMessageId), int32LE(info.bytes), int32LE(info.status))
        : fixedBody(ServiceConstructor.msgDetailedInfo, int64LE(info.messageId), int64LE(info.answerMessageId), int32LE(info.bytes), int32LE(info.status));
};
export const decodeDetailedMessageInfo = (body) => {
    const id = serviceConstructor(body);
    const reader = readerFor(body, id);
    let messageId;
    if (id === ServiceConstructor.msgDetailedInfo)
        messageId = reader.readLong();
    else if (id !== ServiceConstructor.msgNewDetailedInfo)
        throw new RangeError("Unexpected detailed-message constructor");
    const answerMessageId = reader.readLong();
    const bytes = reader.readInt();
    const status = reader.readInt();
    reader.expectEnd();
    if (bytes < 0 || bytes > MAX_PACKET_BYTES || status < 0) {
        throw new RangeError("Invalid detailed message information");
    }
    return { messageId, answerMessageId, bytes, status };
};
export const encodeBadMsgNotification = (badMessageId, badSequenceNumber, errorCode) => fixedBody(ServiceConstructor.badMsgNotification, int64LE(badMessageId), int32LE(badSequenceNumber), int32LE(errorCode));
export const encodeBadServerSalt = (badMessageId, badSequenceNumber, errorCode, newServerSalt) => fixedBody(ServiceConstructor.badServerSalt, int64LE(badMessageId), int32LE(badSequenceNumber), int32LE(errorCode), int64LE(newServerSalt));
export const decodeBadMessageNotification = (body) => {
    if (body.length !== 20 && body.length !== 28)
        throw new RangeError("Invalid bad-message notification");
    const id = readInt32LE(body, 0) >>> 0;
    const salt = id === ServiceConstructor.badServerSalt;
    if (!salt && id !== ServiceConstructor.badMsgNotification)
        throw new RangeError("Unexpected bad-message constructor");
    if (salt !== (body.length === 28))
        throw new RangeError("Invalid bad-message length");
    return {
        kind: salt ? "salt" : "message",
        badMessageId: readInt64LE(body, 4),
        badSequenceNumber: readInt32LE(body, 12),
        errorCode: readInt32LE(body, 16),
        newServerSalt: salt ? readInt64LE(body, 20) : undefined,
    };
};
export const encodePing = (pingId) => fixedBody(ServiceConstructor.ping, int64LE(pingId));
export const encodePingDelayDisconnect = (pingId, delaySeconds) => fixedBody(ServiceConstructor.pingDelayDisconnect, int64LE(pingId), int32LE(delaySeconds));
export const encodePong = (messageId, pingId) => fixedBody(ServiceConstructor.pong, int64LE(messageId), int64LE(pingId));
export const encodeNewSessionCreated = (firstMessageId, uniqueId, serverSalt) => fixedBody(ServiceConstructor.newSessionCreated, int64LE(firstMessageId), int64LE(uniqueId), int64LE(serverSalt));
export const encodeGetFutureSalts = (count) => {
    if (!Number.isInteger(count) || count < 1 || count > 64)
        throw new RangeError("Invalid future-salt count");
    return fixedBody(ServiceConstructor.getFutureSalts, int32LE(count));
};
export const decodeGetFutureSalts = (body) => {
    const reader = readerFor(body, ServiceConstructor.getFutureSalts);
    const count = reader.readInt();
    reader.expectEnd();
    if (count < 1 || count > 64)
        throw new RangeError("Invalid future-salt count");
    return count;
};
export const encodeFutureSalts = (requestMessageId, now, salts) => {
    if (salts.length < 1 || salts.length > 64)
        throw new RangeError("Invalid future-salt count");
    return fixedBody(ServiceConstructor.futureSalts, int64LE(requestMessageId), int32LE(now), encodeTlVector(salts.map((salt) => {
        if (!Number.isInteger(salt.validSince) || !Number.isInteger(salt.validUntil) ||
            salt.validUntil <= salt.validSince)
            throw new RangeError("Invalid future-salt interval");
        return fixedBody(ServiceConstructor.futureSalt, int32LE(salt.validSince), int32LE(salt.validUntil), int64LE(salt.salt));
    })));
};
export const encodeDestroySession = (sessionId) => fixedBody(ServiceConstructor.destroySession, int64LE(sessionId));
export const decodeDestroySession = (body) => {
    const reader = readerFor(body, ServiceConstructor.destroySession);
    const sessionId = reader.readLong();
    reader.expectEnd();
    return sessionId;
};
export const encodeDestroySessionResult = (sessionId, found) => fixedBody(found ? ServiceConstructor.destroySessionOk : ServiceConstructor.destroySessionNone, int64LE(sessionId));
export const encodeDestroyAuthKey = () => fixedBody(ServiceConstructor.destroyAuthKey);
export const encodeDestroyAuthKeyResult = (result) => fixedBody(result === "ok"
    ? ServiceConstructor.destroyAuthKeyOk
    : result === "none"
        ? ServiceConstructor.destroyAuthKeyNone
        : ServiceConstructor.destroyAuthKeyFail);
export const encodeGzipPacked = (packed) => fixedBody(ServiceConstructor.gzipPacked, encodeTlBytes(packed));
export const decodeGzipPacked = (body, gunzip, maximumOutputBytes = MAX_GZIP_OUTPUT_BYTES) => {
    if (maximumOutputBytes < 4 || maximumOutputBytes > MAX_GZIP_OUTPUT_BYTES) {
        throw new RangeError("Invalid gzip output limit");
    }
    const reader = readerFor(body, ServiceConstructor.gzipPacked);
    const packed = reader.readBytes();
    reader.expectEnd();
    const unpacked = gunzip(packed, maximumOutputBytes);
    if (unpacked.length < 4 || unpacked.length > maximumOutputBytes || unpacked.length % 4 !== 0) {
        throw new RangeError("Invalid decompressed MTProto object");
    }
    return unpacked;
};
export const serviceConstructor = (body) => {
    requireAlignedBody(body);
    return readInt32LE(body, 0) >>> 0;
};
const readerFor = (body, expected) => {
    requireAlignedBody(body);
    if ((readInt32LE(body, 0) >>> 0) !== expected)
        throw new RangeError("Unexpected service constructor");
    return new TlReader(body.slice(4));
};
